import React, { useState } from "react";
import { useDispatch } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { useAppStore } from "../../../../providers/AppStore";
import { useAuth } from "../../../../providers/AuthProvider";
import { answersActions } from "../../../questionnaire/store/answers-slice";
import {
  finalizeCustomerLabPanel,
  approveCustomerLabPanel,
  rejectCustomerLabPanel,
  retryLabPanel,
  getHistoryLabPanel,
  GetCustomerPersonalInfo,
} from "../../../../common/network/Api";
import { isBasicInfoValidForLabOrder } from "../../utils/basicInfoValidation";
import { getApiErrorMessage } from "../../../../common/utils/apiError";
import {
  resolveLabPanelStatus,
  getStatusMessageColors,
  isFinalizeFailureStatus,
  getPanelFailureReason,
  EnCustomerPanelStatus,
  STATUS_ACTIONS,
  STATUS_MESSAGES,
  StatusActionKind,
  NO_PAYMENT_METHOD_MESSAGE,
  getPaymentFailureMessage,
} from "../../utils/labPanelStatus";
import InlineBanner, { BannerAction } from "./InlineBanner";
import BasicInformationModal from "./BasicInformationModal";

// RetryLabPanel answers 200 with an empty body and the panel re-enters the
// pipeline (ProcessingPayment) rather than landing on its end state; the lab's
// verdict — OrderReceived or OrderFailed again — arrives a few seconds later.
// The banner polls the panel at this cadence, for at most this long, so the
// affiliate is told the actual outcome instead of "processing".
const RETRY_POLL_INTERVAL_MS = 2000;
const RETRY_POLL_TIMEOUT_MS = 30000;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

interface LabPanelStatusBannerProps {
  // Any lab-panel-shaped object carrying `panelStatus` (+ `id`, `name`). Used by
  // both the order detail (order) and the prescription detail (prescription lab
  // panel), which share the finalize/authorize/retry flow.
  panel: any;
  // Client the panel belongs to — needed for the basic-info validation fetch and
  // the profile modal. In orders this is `order.customerID`; in the prescription
  // it's the selected client's id.
  customerID: string;
  // Called after a successful finalize when the caller wants to leave the view
  // (order detail returns to the list). If omitted, `onRefetch` runs instead so
  // the current view just refreshes in place (prescription detail).
  onApproved?: () => void;
  // Refetch the data in place — used after a failure, a payment-failed 200, and
  // (when there's no onApproved) a success.
  onRefetch?: () => void | Promise<void>;
  // awaiting_details over unanswered order-entry questions: open the editor
  // where the answers are given (the prescription detail's edit-panel modal;
  // the order detail hands over to its prescription). Without it the banner
  // explains the status but offers no button.
  onAnswerQuestions?: () => void;
  marginBottom?: number;
  fontSize?: number;
}

/**
 * The order-status banner + its action buttons (Authorize / Retry / Update
 * details / Approve / Reject, and Retry again for an order the lab failed
 * after payment) for a single lab panel. Encapsulates the finalize flow — payment-
 * method guard, declined-payment (200 + panelStatus=3) handling, and the
 * awaiting-details profile modal — so the order detail and the prescription
 * detail behave identically. Renders nothing when the panel has no status.
 */
const LabPanelStatusBanner: React.FC<LabPanelStatusBannerProps> = ({
  panel,
  customerID,
  onApproved,
  onRefetch,
  onAnswerQuestions,
  marginBottom = 0,
  fontSize = 12,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSelectedClient,
    ASClearSelectedClient,
    ASGetCustomerPersonalInfo,
    ASGetDataForBiometricTab,
    ASGetClientBiomarkerTabDataHandler,
    ASGetDataFromPlatform,
  } = useAppStore();
  const { AuthUser } = useAuth();

  const [isApproving, setIsApproving] = useState(false);
  // Results approve/reject (distinct from the payment authorize/retry above).
  const [isApprovingResults, setIsApprovingResults] = useState(false);
  const [isRejectingResults, setIsRejectingResults] = useState(false);
  // order_failed → re-submitting the paid order to the lab.
  const [isRetryingOrder, setIsRetryingOrder] = useState(false);
  const [showBasicInfoModal, setShowBasicInfoModal] = useState(false);
  // "Retry" for the awaiting-details flow (saving re-submits), "Save" otherwise.
  const [modalPrimaryLabel, setModalPrimaryLabel] = useState("Save");

  const resolvedStatus = resolveLabPanelStatus(panel);

  // Only reached via the modal path (incomplete contact info / awaiting-details).
  // Load the panel client's profile into the shared answers slice so the modal
  // can edit/save it. That slice also backs the Biometrics tab, which is
  // intentional: for the same client, a save here syncs straight through to the
  // tab. We deliberately DON'T clear the slice afterwards — clearing it wiped
  // the Biometrics tab until a reload, and the common (complete-info) authorize
  // path never touches the slice at all.
  const loadClientAnswers = (profile: any) => {
    const info = {
      email: profile.email,
      lastName: profile.lastName,
      firstName: profile.firstName,
      phone: profile.phone,
      countryCode: profile.countryCode,
      countryID: profile.countryID,
      countryName: profile.countryName,
      stateID: profile.stateID,
      stateName: profile.stateName,
      stateCode: profile.stateCode,
      city: profile.city,
      address: profile.address,
      zip: profile.zip,
      report: profile.report,
      workout: profile.workout,
      nutrition: profile.nutrition,
    };
    dispatch(answersActions.updatePersonalInfoInitialAnswers(info));
    dispatch(answersActions.updatePersonalInfoAnswers(info));
  };

  // Reconcile the shared answers slice back to the app's client context after
  // the modal borrowed it (only reached via the modal path, so the common
  // complete-info authorize never runs this):
  // - same client as the selected one → refetch so the Biometrics tab reflects
  //   the profile edit made here;
  // - a different client (e.g. an order opened from the global Orders list) →
  //   clear the selection so the affiliate reselects from the client list,
  //   rather than leaving a page that mixes this client's data with another's.
  const reconcileClientContext = async () => {
    if (customerID && ASSelectedClient?.id === customerID) {
      await ASGetCustomerPersonalInfo(customerID, {
        deferClientSelection: true,
      });
      await ASGetDataForBiometricTab(AuthUser.role);
    } else if (ASSelectedClient?.id) {
      // Clear only this tab's React selection (the per-tab customer session
      // stays; other tabs are unaffected either way since each tab owns its
      // own sessionStorage). The affiliate reselects from the client list.
      ASClearSelectedClient();
    }
  };

  const finalizeOrder = async () => {
    // Without a saved payment method the finalize/charge can't succeed, so bail
    // before hitting FinalizeCustomerLabPanel. Single choke point for both the
    // initial authorize and the payment-failed retry.
    if (AuthUser?.paymentMethod === 0) {
      ASSetTypeSnackBar("alert");
      ASSetSnackBarMessage({
        title: "No payment method",
        message: NO_PAYMENT_METHOD_MESSAGE,
      });
      ASSetOpenSnackBar(true);
      setIsApproving(false);
      return;
    }
    try {
      setIsApproving(true);
      const result = await finalizeCustomerLabPanel(panel.id);

      // A 200 doesn't guarantee the order went through — the backend returns the
      // updated panel, which can come back on any failure status (declined
      // charge, or charged-but-refused-by-the-lab). Report that status's
      // resolved reason so the affiliate sees WHY it failed instead of a
      // generic line — or worse, the success snackbar.
      if (isFinalizeFailureStatus(result)) {
        const failure = resolveLabPanelStatus(result);
        ASSetTypeSnackBar("alert");
        ASSetSnackBarMessage({
          title: failure?.label ?? "Payment failed",
          message: failure?.message || STATUS_MESSAGES.payment_failed,
        });
        ASSetOpenSnackBar(true);
        await onRefetch?.();
        return;
      }

      ASSetTypeSnackBar("success");
      ASSetSnackBarMessage({
        title: "Payment completed",
        message: "The payment for this order was completed successfully.",
      });
      ASSetOpenSnackBar(true);
      // Orders leave to the list via onApproved; the prescription refreshes
      // in place (no onApproved → onRefetch).
      if (onApproved) {
        onApproved();
      } else {
        await onRefetch?.();
      }
    } catch (error: any) {
      // Known Stripe texts (live/test key mismatch, missing customer, declined
      // card…) get friendly copy; anything else is quoted verbatim.
      const backendMessage = getApiErrorMessage(error, "");
      ASSetTypeSnackBar("alert");
      ASSetSnackBarMessage({
        title: "Payment failed",
        message: getPaymentFailureMessage(backendMessage),
      });
      ASSetOpenSnackBar(true);
      // The backend may have changed the status on failure — refresh in place.
      await onRefetch?.();
    } finally {
      setIsApproving(false);
    }
  };

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      // Require complete contact info and a US address, in a state the
      // panel's lab serves, before the lab-panel order can finalize.
      const profile = await GetCustomerPersonalInfo(customerID);
      if (!isBasicInfoValidForLabOrder(profile, [panel.provider])) {
        loadClientAnswers(profile);
        setModalPrimaryLabel("Save");
        setShowBasicInfoModal(true);
        setIsApproving(false);
        return;
      }
      await finalizeOrder();
    } catch (error: any) {
      ASSetTypeSnackBar("alert");
      ASSetSnackBarMessage({
        title: "Error",
        message: getApiErrorMessage(error, "Failed to approve order."),
      });
      ASSetOpenSnackBar(true);
      setIsApproving(false);
    }
  };

  // awaiting_details: open the profile modal prefilled so the affiliate can fix
  // the detail Junction rejected. The modal's "Retry" saves and re-submits.
  const handleUpdateDetails = async () => {
    try {
      setIsApproving(true);
      const profile = await GetCustomerPersonalInfo(customerID);
      loadClientAnswers(profile);
      setModalPrimaryLabel("Retry");
      setShowBasicInfoModal(true);
    } catch (error: any) {
      ASSetTypeSnackBar("alert");
      ASSetSnackBarMessage({
        title: "Error",
        message: getApiErrorMessage(
          error,
          "Failed to load the client's details."
        ),
      });
      ASSetOpenSnackBar(true);
    } finally {
      setIsApproving(false);
    }
  };

  // Refresh in place so the banner reflects the new (approved/rejected) status
  // and the results table updates. Both callers supply onRefetch; onApproved is
  // the fallback for any caller that only wants to leave the view.
  const refreshAfterResultAction = async () => {
    if (onRefetch) {
      await onRefetch();
    } else {
      onApproved?.();
    }
  };

  // approval_needed / completed_rejected → approve the lab results. The backend
  // creates the customer's UserBiomarkers and returns the updated panel.
  const handleApproveResults = async () => {
    try {
      setIsApprovingResults(true);
      await approveCustomerLabPanel(panel.id);
      ASSetTypeSnackBar("success");
      ASSetSnackBarMessage({
        title: "Results approved",
        message:
          "The lab results were approved and added to the client's biomarkers.",
      });
      ASSetOpenSnackBar(true);
      // Approval creates the customer's UserBiomarkers, so pull them into the
      // Biomarker tab and silently refresh the platform data in the background
      // (same pattern as a single-biomarker save). Reject does neither — nothing
      // is written. Guard on customerID since the platform refresh keys off it.
      if (customerID) {
        await ASGetClientBiomarkerTabDataHandler(customerID);
        ASGetDataFromPlatform(customerID, AuthUser?.role, true);
      }
      await refreshAfterResultAction();
    } catch (error: any) {
      ASSetTypeSnackBar("alert");
      ASSetSnackBarMessage({
        title: "Error",
        message: getApiErrorMessage(error, "Failed to approve the results."),
      });
      ASSetOpenSnackBar(true);
    } finally {
      setIsApprovingResults(false);
    }
  };

  // approval_needed → reject the lab results (server-side status update only).
  const handleRejectResults = async () => {
    try {
      setIsRejectingResults(true);
      await rejectCustomerLabPanel(panel.id);
      ASSetTypeSnackBar("success");
      ASSetSnackBarMessage({
        title: "Results rejected",
        message: "The lab results were rejected.",
      });
      ASSetOpenSnackBar(true);
      await refreshAfterResultAction();
    } catch (error: any) {
      ASSetTypeSnackBar("alert");
      ASSetSnackBarMessage({
        title: "Error",
        message: getApiErrorMessage(error, "Failed to reject the results."),
      });
      ASSetOpenSnackBar(true);
    } finally {
      setIsRejectingResults(false);
    }
  };

  // order_failed → the payment went through but Junction rejected or failed
  // the order. RetryLabPanel re-submits it server-side (empty 200), the panel
  // moves to ProcessingPayment, and the lab's verdict follows: OrderReceived
  // when it took the order this time, OrderFailed again otherwise. The button
  // stays on "Retrying…" while the panel is polled for that verdict; the
  // view then refreshes in place so the banner and badge follow the status
  // the backend settled on.
  const handleRetryOrder = async () => {
    try {
      setIsRetryingOrder(true);
      await retryLabPanel(panel.id);

      const deadline = Date.now() + RETRY_POLL_TIMEOUT_MS;
      let latest: any = null;
      for (;;) {
        latest = await getHistoryLabPanel(panel.id);
        const inProgress =
          Number(latest?.panelStatus) ===
          EnCustomerPanelStatus.ProcessingPayment;
        if (!inProgress || Date.now() >= deadline) break;
        await sleep(RETRY_POLL_INTERVAL_MS);
      }

      const resolved = resolveLabPanelStatus(latest);
      const stillProcessing =
        Number(latest?.panelStatus) === EnCustomerPanelStatus.ProcessingPayment;
      if (resolved && isFinalizeFailureStatus(latest)) {
        // Failed again. The lab's own reason is in the panel's statusLog
        // ("Missing required questions", …) — that is what the affiliate
        // needs to act on, so it leads; the status's generic line is the
        // fallback when the backend sent none.
        const reason = getPanelFailureReason(latest);
        ASSetTypeSnackBar("alert");
        ASSetSnackBarMessage({
          title: resolved.label,
          message: reason
            ? `The lab did not accept the order: ${reason}.`
            : resolved.message ||
              "The lab did not accept the order. Please try again later.",
        });
      } else if (stillProcessing || !resolved) {
        // The lab had not answered within the polling window: the order is
        // resubmitted, its verdict will show on the next load.
        ASSetTypeSnackBar("success");
        ASSetSnackBarMessage({
          title: "Order resubmitted",
          message:
            "The order was sent to the lab again and is still being processed. Check back in a moment for its status.",
        });
      } else {
        ASSetTypeSnackBar("success");
        ASSetSnackBarMessage({
          title: "Order submitted",
          message: `The lab accepted the order. Current status: ${resolved.label}.`,
        });
      }
      ASSetOpenSnackBar(true);
      await refreshAfterResultAction();
    } catch (error: any) {
      ASSetTypeSnackBar("alert");
      ASSetSnackBarMessage({
        title: "Error",
        message: getApiErrorMessage(error, "Failed to resubmit the order."),
      });
      ASSetOpenSnackBar(true);
    } finally {
      setIsRetryingOrder(false);
    }
  };

  const resultActionBusy = isApprovingResults || isRejectingResults;

  const actionDefs: Record<StatusActionKind, BannerAction> = {
    authorize: {
      label: isApproving ? "Authorizing…" : "Authorize Payment",
      onClick: handleApprove,
      variant: "primary",
      disabled: isApproving,
    },
    retry: {
      label: isApproving ? "Retrying…" : "Retry",
      onClick: handleApprove,
      variant: "primary",
      disabled: isApproving,
    },
    approve: {
      label: isApprovingResults ? "Approving…" : "Approve",
      onClick: handleApproveResults,
      variant: "primary",
      disabled: resultActionBusy,
    },
    reject: {
      label: isRejectingResults ? "Rejecting…" : "Reject",
      onClick: handleRejectResults,
      variant: "muted",
      disabled: resultActionBusy,
    },
    update_details: {
      label: isApproving ? "Loading…" : "Update details",
      onClick: handleUpdateDetails,
      variant: "primary",
      disabled: isApproving,
    },
    retry_order: {
      label: isRetryingOrder ? "Retrying…" : "Retry",
      onClick: handleRetryOrder,
      variant: "primary",
      disabled: isRetryingOrder,
    },
    answer_questions: {
      label: "Answer questions",
      onClick: () => onAnswerQuestions?.(),
      variant: "primary",
    },
  };

  // Prefer the resolver's dynamic actions (awaiting_details) over the static
  // map. Answering needs an editor to open; a caller without one gets the
  // message alone.
  const bannerActions: BannerAction[] = (
    resolvedStatus
      ? resolvedStatus.actions ?? STATUS_ACTIONS[resolvedStatus.key] ?? []
      : []
  )
    .filter((kind) => kind !== "answer_questions" || !!onAnswerQuestions)
    .map((kind) => actionDefs[kind]);

  if (!resolvedStatus?.message) return null;

  return (
    <>
      <InlineBanner
        text={resolvedStatus.message}
        backgroundColor={
          getStatusMessageColors(resolvedStatus.tone, theme).backgroundColor
        }
        iconColor={theme.colors_ds.NEUTRAL_TEXT_DEFAULT}
        actions={bannerActions}
        marginBottom={marginBottom}
        fontSize={fontSize}
      />
      <BasicInformationModal
        open={showBasicInfoModal}
        clientId={customerID}
        primaryLabel={modalPrimaryLabel}
        labProviders={[panel.provider]}
        onClose={() => {
          setShowBasicInfoModal(false);
          // Discard the borrowed edits and restore the app's client context.
          reconcileClientContext();
        }}
        onSaved={async () => {
          setShowBasicInfoModal(false);
          await finalizeOrder();
          // Sync the Biometrics tab (same client) or clear the selection
          // (different client) now that the profile was saved.
          await reconcileClientContext();
        }}
      />
    </>
  );
};

export default LabPanelStatusBanner;
